import AsyncStorage from '@react-native-async-storage/async-storage';
import { Alert, Platform } from 'react-native';
import * as Notifications from 'expo-notifications';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { acquirePromptLock, releasePromptLock } from '@/services/promptCoordinator';
import { requestNotificationPermissions } from '@/services/notifications';

type ValuePromptMoment = 'template_created' | 'email_sync_success' | 'trip_completed';

type ValuePromptCopy = {
  title: string;
  message: string;
};

const STORAGE_KEY = '@tripvio_notification_value_prompt_v1';
const PROMPT_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

type PromptState = {
  lastPromptAt?: number;
  byMoment?: Partial<Record<ValuePromptMoment, number>>;
};

const readState = async (): Promise<PromptState> => {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PromptState;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed;
  } catch {
    return {};
  }
};

const writeState = async (next: PromptState): Promise<void> => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // best-effort persistence
  }
};

const hasPermissionAlready = async (): Promise<boolean> => {
  try {
    const permissions = await Notifications.getPermissionsAsync();
    return permissions.granted === true;
  } catch {
    return false;
  }
};

const defaultCopyByMoment: Record<ValuePromptMoment, ValuePromptCopy> = {
  template_created: {
    title: 'Stay on top of your itinerary',
    message: 'Enable notifications for day-of reminders and timing nudges for this trip.',
  },
  email_sync_success: {
    title: 'Get booking change alerts',
    message: 'Turn on notifications so Tripvio can alert you about important itinerary and booking updates.',
  },
  trip_completed: {
    title: 'Keep your travel momentum',
    message: 'Enable notifications for recap reminders, rebook ideas, and yearly travel highlights.',
  },
};

export const maybePromptNotificationValue = async (params: {
  moment: ValuePromptMoment;
  source: string;
  copy?: Partial<ValuePromptCopy>;
}): Promise<boolean> => {
  if (Platform.OS === 'web') return false;
  if (!acquirePromptLock('notification_value')) return false;

  try {
    if (await hasPermissionAlready()) return false;

    const state = await readState();
    const now = Date.now();
    const lastGlobalPrompt = state.lastPromptAt ?? 0;
    const lastMomentPrompt = state.byMoment?.[params.moment] ?? 0;
    if (now - Math.max(lastGlobalPrompt, lastMomentPrompt) < PROMPT_COOLDOWN_MS) {
      return false;
    }

    const copy = {
      ...defaultCopyByMoment[params.moment],
      ...params.copy,
    };

    await logAnalyticsEvent('notification_value_prompt_shown', {
      moment: params.moment,
      source: params.source,
    });

    return await new Promise<boolean>((resolve) => {
      Alert.alert(copy.title, copy.message, [
        {
          text: 'Not now',
          style: 'cancel',
          onPress: () => {
            void logAnalyticsEvent('notification_value_prompt_denied', {
              moment: params.moment,
              source: params.source,
              reason: 'user_cancelled',
            });
            void writeState({
              ...state,
              lastPromptAt: now,
              byMoment: {
                ...(state.byMoment ?? {}),
                [params.moment]: now,
              },
            });
            resolve(false);
          },
        },
        {
          text: 'Enable',
          onPress: () => {
            void (async () => {
              const granted = await requestNotificationPermissions();
              await logAnalyticsEvent(granted ? 'notification_value_prompt_granted' : 'notification_value_prompt_denied', {
                moment: params.moment,
                source: params.source,
                ...(granted ? {} : { reason: 'os_denied' as const }),
              });
              await writeState({
                ...state,
                lastPromptAt: now,
                byMoment: {
                  ...(state.byMoment ?? {}),
                  [params.moment]: now,
                },
              });
              resolve(granted);
            })();
          },
        },
      ]);
    });
  } finally {
    releasePromptLock('notification_value');
  }
};
